import React from 'react';
import { motion } from 'framer-motion';

// Page transition wrapper
export const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

// Staggered list animation
export const StaggerContainer = ({ children, className = '', delay = 0 }) => {
  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            delayChildren: delay,
            staggerChildren: 0.1
          }
        }
      }}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = '' }) => {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
      }}
    >
      {children}
    </motion.div>
  );
};

// Fade in animation
export const FadeIn = ({ children, delay = 0, className = '' }) => {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.5 }}
    >
      {children}
    </motion.div>
  );
};

// Scale on hover button
export const AnimatedButton = ({ children, className = '', onClick, disabled, ...props }) => {
  return (
    <motion.button
      className={className}
      onClick={onClick}
      disabled={disabled}
      whileHover={{ scale: disabled ? 1 : 1.02, y: disabled ? 0 : -2 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      {...props}
    >
      {children}
    </motion.button>
  );
};

// Animated card with hover effect
export const AnimatedCard = ({ children, className = '', onClick }) => {
  return (
    <motion.div
      className={className}
      onClick={onClick}
      whileHover={{ 
        scale: 1.02, 
        y: -4,
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      {children}
    </motion.div>
  );
};

// Pulse animation for notifications/badges
export const PulseAnimation = ({ children, className = '' }) => {
  return (
    <motion.div
      className={className}
      animate={{ 
        scale: [1, 1.05, 1],
        opacity: [1, 0.8, 1]
      }}
      transition={{ 
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }}
    >
      {children}
    </motion.div>
  );
};

// Skeleton loader component
export const Skeleton = ({ className = '', variant = 'rectangular' }) => {
  const baseClasses = 'animate-pulse bg-green-800/50';
  const variantClasses = {
    rectangular: 'rounded-lg',
    circular: 'rounded-full',
    text: 'rounded h-4'
  };
  
  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`} />
  );
};

// Skeleton card
export const SkeletonCard = () => {
  return (
    <div className="bg-green-900/50 border border-green-700 rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-4">
        <Skeleton variant="circular" className="w-12 h-12" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <Skeleton className="h-20 w-full" />
      <div className="flex gap-2">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
      </div>
    </div>
  );
};

// Shimmer effect
export const Shimmer = ({ className = '' }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <motion.div
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent"
        animate={{ translateX: ['100%', '-100%'] }}
        transition={{ 
          duration: 1.5, 
          repeat: Infinity,
          ease: 'linear'
        }}
      />
    </div>
  );
};

// Number counter animation
export const AnimatedNumber = ({ value, prefix = '', suffix = '', className = '' }) => {
  return (
    <motion.span
      className={className}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      key={value}
    >
      {prefix}{value}{suffix}
    </motion.span>
  );
};

// Success checkmark animation
export const SuccessCheckmark = ({ size = 60 }) => {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
    >
      <motion.svg
        width={size}
        height={size}
        viewBox="0 0 60 60"
        initial="hidden"
        animate="visible"
      >
        <motion.circle
          cx="30"
          cy="30"
          r="28"
          fill="none"
          stroke="#10B981"
          strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5 }}
        />
        <motion.path
          d="M18 30 L26 38 L42 22"
          fill="none"
          stroke="#10B981"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        />
      </motion.svg>
    </motion.div>
  );
};

export default {
  PageTransition,
  StaggerContainer,
  StaggerItem,
  FadeIn,
  AnimatedButton,
  AnimatedCard,
  PulseAnimation,
  Skeleton,
  SkeletonCard,
  Shimmer,
  AnimatedNumber,
  SuccessCheckmark
};
